/* eslint-disable @typescript-eslint/no-explicit-any */
import * as angular from 'angular';
import type { ICompileProvider, IHttpProvider, ILocationProvider, IQProvider, IHttpInterceptor, material, auto } from 'angular';
import type { StateRegistry } from '@uirouter/core/lib/state/stateRegistry';
import type { Ng1StateDeclaration } from '@uirouter/angularjs/lib/interface';
import '@fontsource/roboto';
import * as ngMaterial from 'angular-material';
import 'angular-material/angular-material.min.css';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as angularMoment from 'angular-moment';
import * as ngSanitize from 'angular-sanitize';
import 'angular-hotkeys';
import 'angular-hotkeys/build/hotkeys.min.css';
import * as moment from 'moment';
import * as ngFileUpload from 'ng-file-upload';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import 'angular-ui-grid/ui-grid';
import uiRouter, { UrlService } from '@uirouter/angularjs';
import type { UIRouter } from '@uirouter/core/lib/router';
import * as oclazyload from 'oclazyload';
import 'angular-gridster';
import 'angular-gridster/dist/angular-gridster.css';
import 'angular-ui-grid/ui-grid.min.css';
import 'ui-grid-draggable-rows';

import { utilsModule } from '../Utils/mx-utils.module.js';
import { signupRootState } from '../SignUp/signup.states';
import { initShellState } from '../Shell/shell.states';
import { confirmPasswordState, resetPasswordState, resetPasswordVerificationState } from '../ResetPassword/resetPassword.states';

import { authModule } from '../Auth/auth.module';
import { i18nModule } from '../i18n/i18n.module';
import { appState, authAppState, defaultAppState } from './app.states';
import { MxAppComponent } from './mx-app.component';

import { UserProfile } from './model/user-profile';
import { EventCodes } from './model/event-codes';
import { RoutingItem } from './model/routing-item';

import { ConfigService } from './services/mx-config-service';
import { DataStorage } from './services/mx-data-storage';
import { EventService } from './services/event-service';
import { httpInterceptorsRegistry, IHttpInterceptorsRegistry } from './services/http-interceptors-registry';
import { HttpService, httpServiceFactory } from './services/http-service';
import { AriaService } from './services/mx-aria-service';
import { NotificationService } from './services/mx-notification-service';
import { HostUrlHelper } from './services/host-url-helper';
import { UserProfileService } from './services/user-profile-service';
import { UserStorageService } from './services/user-storage-service';
import { UserRegistrationModule } from '../UserRegistration/user-registration.module';
import { RoutingServiceProvider } from './services/mx-routing-service';
import { PersonalizationService } from './services/personalization-service';
import { objectDetailsPageState, printObjectDetailsState } from '../ObjectDetailsPage/object-details-page.states';
import { helpArticleState } from '../HelpArticlePage/helpArticlePage.states';
import { http503Interceptor } from './services/http503Interceptor';
import { errorPageState } from '../ErrorPage/error-page.states';
import { externalAuthState } from '../ExternalAuth/externalAuth.states';
import { errorPageModule } from '../ErrorPage/errorPage.module';

import { designerAppState, designerLayoutState } from '../Designer/designer.states';

import { mx } from './globals';

import { I18n } from '../i18n/mx-internationalization';
import { workspaceLoaderModule } from '../WorkspaceLoader/workspace-loader.module';
import { ErrorFactory } from './services/ErrorFactory';
import { DeviceService } from './services/device-service';
import { MxApplicationService } from './services/mx-application-service';
import { MxFeatureTrackingService } from './services/mx-feature-tracking-service';

import { telemetryModule } from '../Telemetry/telemetry.module';
import { themesModule } from '../Theme/themes.module';
import { KeyboardNavigationService, KeyboardNavigationTeplate } from './services/mx-keyboard-navigation';
import type { HotkeysProvider } from 'angular-hotkeys';
import { mxNavigationModule } from '../Navigation/navigation.module';
import { DownloadHelpService } from './services/mx-download-help-service';
import { DataQueryService } from './services/data-query-service';
import { novaModule } from '../Nova/nova.module';
import { printingModule } from '../Print/print.module';
import { reportsModule } from '../Dashboard/reports.module';
import { UserlaneService } from './services/mx-userlane-service';

window.moment = moment;

export default angular
	.module('mx.app', [
		ngFileUpload,
		'ui.grid',
		'ui.grid.resizeColumns',
		'ui.grid.moveColumns',
		'ui.grid.edit',
		'ui.grid.cellNav',
		'ui.grid.expandable',
		'ui.grid.draggable-rows',
		authModule.name,
		uiRouter,
		ngMaterial,
		oclazyload,
		utilsModule,
		angularMoment,
		ngSanitize,
		'cfp.hotkeys',
		UserRegistrationModule.name,
		i18nModule.name,
		errorPageModule.name,
		workspaceLoaderModule.name,
		telemetryModule.name,
		themesModule.name,
		mxNavigationModule.name,
		novaModule.name,
		printingModule.name,
		reportsModule.name,
	])
	.component(appState.component, MxAppComponent)
	.provider('mx.shell.RoutingService', RoutingServiceProvider)
	.service('mx.shell.Config', ConfigService)
	.service('mx.shell.HostUrlHelper', HostUrlHelper)
	.service('mx.shell.DataStorage', DataStorage)
	.service('mx.shell.EventService', EventService)
	.service('mx.shell.NotificationService', NotificationService)
	.service('mx.shell.UserProfileService', UserProfileService)
	.service('mx.SolutionBuilderAgent.PersonalizationService', PersonalizationService)
	.factory('mx.shell.Http', httpServiceFactory)
	//deprecated
	.factory('mx.SolutionBuilderAgent.Http', [
		'mx.shell.Http',
		function deprecatedAgentHttp(http: HttpService) {
			return http;
		},
	])

	.service('mx.SolutionBuilderAgent.UserStorageService', UserStorageService)
	.service('mx.app.ErrorFactory', ErrorFactory)
	.service('mx.shell.AppsService', MxApplicationService)
	.service('mx.app.DeviceService', DeviceService)
	.factory('mx.app.Http503Interceptor', http503Interceptor)
	.factory('mx.app.HttpInterceptorsRegistry', httpInterceptorsRegistry)
	.service('mx.shell.AriaService', AriaService)
	.service('mx.shell.KeyboardNavigationService', KeyboardNavigationService)
	.service('mx.shell.DownloadHelpService', DownloadHelpService)
	.service('mx.SolutionBuilderAgent.DataQueryService', DataQueryService)
	.service('mx.shell.UserlaneService', UserlaneService)
	.service('mx.shell.FeatureTrackingService', MxFeatureTrackingService)

	.value('mx.shell.UserProfile', UserProfile)
	.value('mx.shell.EventCodes', EventCodes)
	.value('mx.shell.RoutingItem', RoutingItem)

	.config(configApp)
	.run(runApp).name;

configApp.$inject = [
	'$uiRouterProvider',
	'$qProvider',
	'$locationProvider',
	'$rootScopeProvider',
	'$compileProvider',
	'$httpProvider',
	'$ocLazyLoadProvider',
	'$mdAriaProvider',
	'$mdThemingProvider',
	'$animateProvider',
	'$mdCompilerProvider',
	'hotkeysProvider',
	'$sceDelegateProvider',
];

function configApp(
	$uiRouterProvider: UIRouter,
	$qProvider: IQProvider,
	$locationProvider: ILocationProvider,
	$rootScopeProvider: any,
	$compileProvider: ICompileProvider,
	$httpProvider: IHttpProvider,
	$ocLazyLoadProvider: oc.ILazyLoadProvider,
	$mdAriaProvider: material.IAriaProvider,
	$mdThemingProvider: material.IThemingProvider,
	$animateProvider: any,
	$mdCompilerProvider: any,
	hotkeysProvider: HotkeysProvider,
	$sceDelegateProvider: angular.ISCEDelegateProvider
) {
	$mdAriaProvider.disableWarnings();

	$mdCompilerProvider.respectPreAssignBindingsEnabled(true);

	$mdThemingProvider.disableTheming();
	$animateProvider.classNameFilter(/mx-animate/);

	if (Array.isArray(mx.shell.config.linkAllowProtocols)) {
		const re = new RegExp('^\\s*(https?|' + mx.shell.config.linkAllowProtocols.join('|').toLowerCase() + '):');
		$compileProvider.aHrefSanitizationTrustedUrlList(re);
	}

	$ocLazyLoadProvider.config({ events: true, debug: false });

	$qProvider.errorOnUnhandledRejections(mx.isDebugMode);
	$locationProvider.html5Mode(true).hashPrefix('!');

	// Default: 10
	// For some special cases, where "smart"-components modify data recursively. (e.g. SSP -> complex shopping-cart)
	$rootScopeProvider.digestTtl(15);

	// Performance improvement
	$compileProvider.debugInfoEnabled(mx.isDebugMode);
	$compileProvider.commentDirectivesEnabled(false);
	$compileProvider.cssClassDirectivesEnabled(false);
	$httpProvider.useApplyAsync(true);

	$httpProvider.interceptors.push('mx.app.HttpInterceptorsRegistry');

	$uiRouterProvider.urlService.config.strictMode(false);
	$uiRouterProvider.urlService.config.caseInsensitive(false);
	// $uiRouterProvider.trace.enable(1); //enable router debug states
	initStates($uiRouterProvider.stateRegistry, $uiRouterProvider.urlService);
	hotkeysProvider.template = KeyboardNavigationTeplate;

	if (mx.isDebugMode) {
		$sceDelegateProvider.trustedResourceUrlList(['self', 'http://localhost:3000/**']);
	}
}

function initStates($stateRegistry: StateRegistry, $urlService: UrlService) {
	const states = [
		initShellState,
		signupRootState,
		resetPasswordState,
		resetPasswordVerificationState,
		confirmPasswordState,
		objectDetailsPageState,
		printObjectDetailsState,
		helpArticleState,
		errorPageState,
		externalAuthState,
		designerAppState,
	] as Ng1StateDeclaration[];

	states.forEach((state) => {
		const authState = !state.data?.isAnonymous;
		state.parent = authState ? authAppState.name : appState.name;
		$stateRegistry.register(state);
	});

	$stateRegistry.register(designerLayoutState);

	$stateRegistry.register(defaultAppState);
	$stateRegistry.register(authAppState);
	$stateRegistry.register(appState);

	$urlService.rules.otherwise({ state: defaultAppState.name });
}

runApp.$inject = ['mx.internationalization', 'amMoment', '$mdDateLocale', '$injector', 'mx.app.HttpInterceptorsRegistry', 'mx.app.Http503Interceptor'];

async function runApp(
	i18n: I18n,
	amMoment: any,
	$mdDateLocale: material.IDateLocaleProvider,
	$injector: auto.IInjectorService,
	httpInterceptorsRegistry: IHttpInterceptorsRegistry,
	http503Interceptor: IHttpInterceptor
) {
	mx.shell.Injector = $injector;

	amMoment.changeLocale(i18n.language);
	// i18n.localization = mx.localization;

	$mdDateLocale.formatDate = function (date) {
		return i18n.formatDate(date);
	};

	$mdDateLocale.parseDate = function (dateString) {
		return i18n.parseDate(dateString);
	};

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	$mdDateLocale.isDateComplete = function (dateString) {
		dateString = dateString.trim();
		// Default regex of material :
		// Looks for three chunks of content (either numbers or text) separated by delimiters.
		// and additional regex for 6-8 digits
		const re = /^((([a-zA-Z]{3,}|[0-9]{1,4})([ .,]+|[/-])){2}([a-zA-Z]{3,}|[0-9]{1,4}))|(\d{6}|\d{8})$/;
		return re.test(dateString);
	};

	httpInterceptorsRegistry.register(http503Interceptor);

	// Suppress console errors from SUPERSEDED transitions (type 2), like: "Transition Rejection(... type: 2, message: The transition has been superseded by a different transition, detail: Transition..."
	// These happen any time a transition is redirected via TargetState (e.g. the auth module's post-login retarget) and are expected/benign — not true errors.
	const $state = $injector.get('$state') as { defaultErrorHandler: (fn: (error: { type?: number }) => void) => void };
	$state.defaultErrorHandler((error) => {
		if (error?.type === 2) return; // SUPERSEDED — benign redirect, not an error
		console.error(error);
	});
}
